import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { SHIPPING_FEES } from '@consts/index'
import { checkStoreIs } from '@helpers/env'
import { cartViewedEvent } from '@helpers/cart'
import currencyConverter from '@helpers/misc/currencyConverter'
import {
  findCustomAttribute,
  sortLineItemsByCustomAttribute
} from '@helpers/cart/customAttributes'
import { ReactComponent as CloseIcon } from '@svgs/keyline/close.svg'
import { ReactComponent as CartIcon } from '@svgs/keyline/menu-cart.svg'
import { SubmitButton } from '@components/ui/Button'
import ErrorBoundary from '@components/ErrorBoundary'
import { useCart } from '../hooks'
import CartProductsList from '../shared/CartProductsList'
import useSuggestedProducts from '../SuggestedProducts/hooks'
import CartTotal from './CartTotal'
import CartNotification from './CartNotification'
import GiftMessage from './GiftMessage'
import GiftMessageButton from './GiftMessage/GiftMessageButton'
import EmptyCart from './EmptyCart'
import styles from './styles.module.scss'
import DiscountCode from './DiscountCodeWrapper'
import { CartDiscountAllocation, CartLineItem } from 'types/checkout'
import Notification from '@components/ui/Notification'
import { InvalidCheckoutNotification } from '@components/FlashBanner/PushNotificationBanner'
import { getCookieByName } from '@helpers/session'

export const FREE_SHIPPING_DISCOUNT_CODE = 'FIRSTSHIP'

export function hasFreeShippingExperiment(): boolean {
  return (
    getCookieByName('discount_code') === FREE_SHIPPING_DISCOUNT_CODE &&
    checkStoreIs('gb')
  )
}

const SuggestedProducts = dynamic(() => import('../SuggestedProducts'), {
  ssr: false
})

function getFreeShippingMethodState({
  total,
  threshold
}: {
  total: number
  threshold: number
}) {
  if (checkStoreIs('eu')) {
    return total <= threshold
  }
  return total >= threshold
}

function getTotalDiscount(discountAllocations: CartDiscountAllocation[]) {
  return discountAllocations.reduce(
    (total: number, discount: CartDiscountAllocation) =>
      total + Number(discount.discountedAmount.amount),
    0
  )
}

function CartSideDrawer({ closeCart }: { closeCart: () => void }) {
  const [showGiftMessageModal, setShowGiftMessageModal] = useState(false)
  const {
    cart,
    cartBlockingErrors,
    lines,
    updateCartQuantity,
    isLoadingCartItems,
    addToCart,
    updateGiftMessage,
    navigateToCheckout
  } = useCart()

  const { suggestedProducts, multibuyDiscount } = useSuggestedProducts(cart)

  const giftMessage = findCustomAttribute(cart.attributes, 'gift_message')
  const hasGiftCardLineItem = !!lines.find(lineItem =>
    lineItem.merchandise.product.productType.includes('Gift Card')
  )
  const storeShippingFeesThreshold =
    SHIPPING_FEES.threshold[process.env.SHOP_LOCALE]

  useEffect(() => {
    if (cart?.id) {
      cartViewedEvent(cart)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isCartLineItemMTO = (item: CartLineItem) => {
    return item.attributes.find(attr => attr.key === '_mto')?.value === 'true'
  }

  const excessQuantity = lines.some(
    item =>
      item.merchandise.quantityAvailable < item.quantity &&
      !isCartLineItemMTO(item)
  )
  const { discountAllocations, discountCodes } = cart
  const totalDiscounts = getTotalDiscount(discountAllocations)
  const totalPrice = cart.cost.totalAmount
  const subtotalPrice = cart.cost.subtotalAmount
  const subtotalAmountMinusDiscounts =
    Number(subtotalPrice.amount) - totalDiscounts
  const lineItemsTotal = lines.reduce((total, item) => total + item.quantity, 0)
  const isCartEmpty = lineItemsTotal === 0
  const hasCustomsFee = checkStoreIs('us')

  const freeShippingOverride =
    hasFreeShippingExperiment() && subtotalAmountMinusDiscounts > 50

  const isFreeShippingMethod =
    freeShippingOverride ||
    getFreeShippingMethodState({
      total: subtotalAmountMinusDiscounts,
      threshold: storeShippingFeesThreshold.value
    })

  const sortedLineItems = sortLineItemsByCustomAttribute(
    lines,
    '_addedToCartTimestamp'
  )

  const shouldShowSuggestedProducts =
    !!lines?.length && !!suggestedProducts?.length

  const discountCode = discountCodes?.find(
    code => code.applicable && code.code
  )?.code

  return (
    <div className={styles.cart} data-testid="cart-overlay">
      {showGiftMessageModal && (
        <GiftMessage
          onClose={() => {
            setShowGiftMessageModal(false)
          }}
          giftMessage={giftMessage || ''}
          onSubmit={value => {
            updateGiftMessage(value)
            setShowGiftMessageModal(false)
          }}
        />
      )}
      <div className={styles.cartScrollContainer}>
        <div className={`${styles.cart__wrapper} ${styles.cart__header}`}>
          <button
            onClick={closeCart}
            type="button"
            className={styles.cart__closeButton}
            aria-label="Close cart"
          >
            <CloseIcon />
          </button>
          <p className={styles.cart__quantity} data-total={lineItemsTotal}>
            <CartIcon className="CartOverlayIcon" aria-label="cart icon" />
          </p>
        </div>

        <CartNotification
          showUsNotification={hasCustomsFee}
          showFreeShippingMethod={isFreeShippingMethod}
          storeShippingFeesThreshold={storeShippingFeesThreshold}
          totalPrice={subtotalAmountMinusDiscounts}
          isCartEmpty={isCartEmpty}
        />
        {cartBlockingErrors?.length && (
          <Notification
            theme="dark"
            type="error"
            content={<InvalidCheckoutNotification />}
          />
        )}
        <div
          className={`${styles.cart__wrapper} ${styles.cart__body} ${
            isCartEmpty ? styles.cart__body_empty : ''
          }`}
        >
          {isCartEmpty ? (
            <EmptyCart />
          ) : (
            <CartProductsList
              updateCartQuantity={updateCartQuantity}
              lineItems={sortedLineItems}
              layout="small"
            />
          )}
        </div>
        {!isCartEmpty && (
          <>
            <div className={`${styles.cart__wrapper} ${styles.cart__footer}`}>
              {!hasGiftCardLineItem && (
                <GiftMessageButton
                  setShowGiftMessageModal={setShowGiftMessageModal}
                  hasGiftMessage={!!giftMessage}
                  updateGiftMessage={updateGiftMessage}
                />
              )}

              {discountCode && (
                <DiscountCode
                  amount={
                    discountAllocations?.length &&
                    currencyConverter({
                      price: getTotalDiscount(discountAllocations),
                      currency: totalPrice.currencyCode
                    })
                  }
                  code={discountCode}
                />
              )}
              <CartTotal
                total={currencyConverter({
                  price: subtotalAmountMinusDiscounts,
                  currency: subtotalPrice.currencyCode
                })}
                isFreeDelivery={isFreeShippingMethod}
              />
              <SubmitButton
                onClick={navigateToCheckout}
                disabled={
                  isLoadingCartItems || excessQuantity || !cart.checkoutUrl
                }
                fullWidth="always"
                classes={styles.cart__submitButton}
              >
                Checkout
              </SubmitButton>
            </div>
            {shouldShowSuggestedProducts && (
              <div className={styles.suggestedProductsWrapper}>
                <ErrorBoundary>
                  <SuggestedProducts
                    products={suggestedProducts}
                    lineItems={sortedLineItems}
                    addToCart={addToCart}
                    disabled={isLoadingCartItems}
                    multibuy={multibuyDiscount}
                  />
                </ErrorBoundary>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CartSideDrawer
